import { Inflate, Unzlib } from 'fflate';

import { ImportError } from './errors';

/** Decompressed payloads larger than this are rejected (SPEC §4.1, §10). */
export const MAX_PAYLOAD_BYTES = 64 * 1024;

const encoder = new TextEncoder();

export function utf8Encode(text: string): Uint8Array<ArrayBuffer> {
  return encoder.encode(text);
}

/** Strict UTF-8 decoding: throws `corrupt` on invalid sequences. */
export function utf8Decode(bytes: Uint8Array): string {
  try {
    return new TextDecoder('utf-8', { fatal: true, ignoreBOM: false }).decode(bytes);
  } catch (cause) {
    throw new ImportError('corrupt', 'Invalid UTF-8', { cause });
  }
}

/** Each byte is one code point U+0000–U+00FF. */
export function latin1Decode(bytes: Uint8Array): string {
  let text = '';
  for (let i = 0; i < bytes.length; i += 4096) {
    text += String.fromCharCode(...bytes.subarray(i, i + 4096));
  }
  return text;
}

export function latin1Encode(text: string): Uint8Array<ArrayBuffer> {
  const bytes = new Uint8Array(text.length);
  for (let i = 0; i < text.length; i++) bytes[i] = text.charCodeAt(i) & 0xff;
  return bytes;
}

const B64 = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_';
const B64_LOOKUP = new Map(Array.from({ length: 64 }, (_, i) => [B64.charAt(i), i]));

/** RFC 4648 §5 base64url without padding. */
export function base64UrlEncode(bytes: Uint8Array): string {
  let out = '';
  for (let i = 0; i < bytes.length; i += 3) {
    const n = ((bytes[i] ?? 0) << 16) | ((bytes[i + 1] ?? 0) << 8) | (bytes[i + 2] ?? 0);
    const chars = Math.min(4, Math.ceil(((bytes.length - i) * 8) / 6));
    for (let j = 0; j < chars; j++) out += B64.charAt((n >> (18 - 6 * j)) & 63);
  }
  return out;
}

/** Decodes base64url (padding optional); throws `corrupt` on invalid input. */
export function base64UrlDecode(text: string): Uint8Array<ArrayBuffer> {
  const clean = text.replace(/=+$/, '');
  if (clean.length % 4 === 1) throw new ImportError('corrupt', 'Invalid base64 length');
  const bytes = new Uint8Array(Math.floor((clean.length * 6) / 8));
  let buffer = 0;
  let bits = 0;
  let index = 0;
  for (const char of clean) {
    const value = B64_LOOKUP.get(char);
    if (value === undefined) throw new ImportError('corrupt', `Invalid base64 character ${char}`);
    buffer = ((buffer << 6) | value) & 0xffffff;
    bits += 6;
    if (bits >= 8) {
      bits -= 8;
      bytes[index++] = (buffer >> bits) & 0xff;
    }
  }
  return bytes;
}

/**
 * Inflates raw DEFLATE (`raw`) or zlib-wrapped (`zlib`) data, failing with
 * `too-large` as soon as the output exceeds `limit` bytes. Input is fed in
 * small slices so a decompression bomb can't allocate much before it's caught.
 */
export function inflateLimited(
  data: Uint8Array,
  format: 'raw' | 'zlib',
  limit = MAX_PAYLOAD_BYTES,
  dictionary?: Uint8Array,
): Uint8Array<ArrayBuffer> {
  const chunks: Uint8Array[] = [];
  const state = { total: 0, done: false };
  const onData = (chunk: Uint8Array, final: boolean) => {
    state.total += chunk.length;
    if (state.total > limit) {
      throw new ImportError('too-large', `Payload exceeds ${String(limit)} bytes`);
    }
    chunks.push(chunk);
    if (final) state.done = true;
  };
  if (data.length === 0) throw new ImportError('corrupt', 'Empty compressed data');
  const stream =
    format === 'raw' ? new Inflate(dictionary ? { dictionary } : {}, onData) : new Unzlib(onData);
  try {
    const step = 1024;
    for (let i = 0; i < data.length; i += step) {
      stream.push(data.subarray(i, i + step), i + step >= data.length);
    }
  } catch (cause) {
    if (cause instanceof ImportError) throw cause;
    throw new ImportError('corrupt', 'Invalid compressed data', { cause });
  }
  if (!state.done) throw new ImportError('corrupt', 'Truncated compressed data');
  return concatBytes(chunks);
}

export function concatBytes(chunks: readonly Uint8Array[]): Uint8Array<ArrayBuffer> {
  const out = new Uint8Array(chunks.reduce((sum, chunk) => sum + chunk.length, 0));
  let offset = 0;
  for (const chunk of chunks) {
    out.set(chunk, offset);
    offset += chunk.length;
  }
  return out;
}
